import { LoggerCategoryEnum } from '@nab/nab-x-sdk-core';
import { sdkLoggerInterface as log } from '../Logger/Logger';
import { IEventMgr } from './IEventMgr';

/**
 * [[include:packages/consumer/sdk-browser/src/EventMgr/README.md]]
 */
class EventMgr implements IEventMgr {
    private eventNamespace: string;

    private miniappName: string;

    private allowedSubscribeEvents: Array<string>;

    private allowedPublishEvents: Array<string>;

    private target: EventTarget;

    /**
     * construct event manager.  The intent is to externalize the allowed pub/sub event types as metadata
     *
     * @deprecated since version 12.0.0; Please use event related methods via `actions` from `MiniAppContext` instead. `@nab/x-runtime` comes with pre-configured event bus instance which is used by these actions. For more info, please refer the {@link https://nab-x-sdk.nabx.lz002.awsnp.national.com.au/modules/_nab_x_events.html guide}.
     *
     * @param miniappName
     * @param allowedSubscribeEvents
     * @param allowedPublishEvents
     * @param target default value: `window`.
     *
     * Warn: In IE 11 and some older browsers (Safari 9, Firefox 52 etc) where custom element is poly filled,
     * shadowRoot is not a concrete EventTarget. it should not be used as the target parameter.
     * Use the web component instance instead.
     * @returns {EventMgr}
     * @example
     * ```
     * const eventMgr = new EventMgr('miniapp-name', ['subscribeEvent'], ['publishEvent']);
     *
     * const scopedEventMgr = new EventMgr('miniapp-name', ['subscribeEvent'], ['publishEvent'], DOMTarget);
     *
     * ```
     *
     */
    constructor(miniappName: string, allowedSubscribeEvents: Array<string>, allowedPublishEvents: Array<string>, target: EventTarget = window) {
        // log.info({ miniapp: miniappName });
        if (!miniappName || !allowedSubscribeEvents || !allowedPublishEvents) {
            log.error(LoggerCategoryEnum.diagnostic, 'nab-x-sdk::EventMgr invalid parameters');
        }
        this.miniappName = miniappName;
        this.allowedSubscribeEvents = allowedSubscribeEvents || [];
        this.allowedPublishEvents = allowedPublishEvents || [];
        this.target = target;
        this.eventNamespace = 'nabx';
    }

    /**
     * subscribe to an event type
     *
     * @deprecated since version 12.0.0; Please use `addEventListener` instead via app actions. For more info, please refer the {@link https://nab-x-sdk.nabx.lz002.awsnp.national.com.au/modules/_nab_x_events.html guide}.
     *
     * @param eventName
     * @param fn
     * @returns unsubscribe function
     * @example
     * ```
     * eventMgr.subscribe('subscribeEvent', (event) => { // handler code });
     * ```
     */
    public subscribe(eventName: string, fn: EventListener) {
        if (this.allowedSubscribeEvents.indexOf(eventName) === -1) {
            log.warn(
                LoggerCategoryEnum.diagnostic,
                `nab-x-sdk::EventMgr ${this.miniappName} attempted undeclared subscription to ${this.eventNamespace}:${eventName}`
            );
        }

        log.info(LoggerCategoryEnum.diagnostic, `nab-x-sdk::EventMgr ${this.miniappName} subscribing to ${eventName}`);

        this.target.addEventListener(this.prefix(eventName), fn);

        return () => {
            this.unsubscribe(eventName, fn);
        };
    }

    /**
     * publish a custom event, this event will be bubbled up to window.
     *
     * @deprecated since version 12.0.0; Please use `dispatchEvent` instead via app actions. For more info, please refer the {@link https://nab-x-sdk.nabx.lz002.awsnp.national.com.au/modules/_nab_x_events.html guide}.
     *
     * @param eventName
     * @param data
     * @returns {any}
     * @example
     * ```
     * eventMgr.publish('publishEvent', { 'someData': 123});
     * ```
     */
    public publish<T>(eventName: string, detail: T) {
        if (this.allowedPublishEvents.indexOf(eventName) === -1) {
            return log.warn(
                LoggerCategoryEnum.diagnostic,
                `nab-x-sdk::EventMgr ${this.miniappName} attempted undeclared publish to ${this.eventNamespace}:${eventName}`
            );
        }
        const prefixedEventName = this.prefix(eventName);
        const data: CustomEventInit<T> = {
            bubbles: true,
            composed: true,
            detail
        };

        const customEvent = new CustomEvent<T>(prefixedEventName, data);

        log.info(LoggerCategoryEnum.diagnostic, `nab-x-sdk::EventMgr ${this.miniappName} sending ${prefixedEventName} detail: `, customEvent.detail);

        return this.target.dispatchEvent(customEvent);
    }

    /**
     * unsubscribe from an event type
     *
     * @deprecated since version 12.0.0; Please use `removeEventListener` instead via app actions. For more info, please refer the {@link https://nab-x-sdk.nabx.lz002.awsnp.national.com.au/modules/_nab_x_events.html guide}.
     *
     * @param eventName
     * @param fn
     * @example
     * ```
     * eventMgr.unsubscribe('subscribeEvent', handlerFunctionRef);
     * ```
     */
    public unsubscribe(eventName: string, fn: EventListenerOrEventListenerObject) {
        log.info(LoggerCategoryEnum.diagnostic, `nab-x-sdk::EventMgr ${this.miniappName} unsubscribing from ${this.eventNamespace}:${eventName}`);

        this.target.removeEventListener(this.prefix(eventName), fn);
    }

    private prefix(eventName: string) {
        return `${this.eventNamespace}:${eventName}`;
    }
}

export { EventMgr, IEventMgr };
